import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Nav, Button, Badge, Alert, Modal, Dropdown, Spinner } from 'react-bootstrap';
import SkeletonLoader, { ProfilePageSkeleton } from '../components/common/SkeletonLoader';
import { profileService, ProfileData, Pass, Booking, Order, AddonOrder, Address } from '../services/services/profileService';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import './ProfilePage.scss';

const ProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alert } = useConfirmation();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('passes');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [passes, setPasses] = useState<Pass[]>([]);
  const [bookings, setBookings] = useState<{ upcoming: Booking[]; past: Booking[] }>({ upcoming: [], past: [] });
  const [orders, setOrders] = useState<Order[]>([]);
  const [addons, setAddons] = useState<AddonOrder[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedPass, setSelectedPass] = useState<Pass | null>(null);
  const [qrCodeData, setQrCodeData] = useState<{ qr_code: string; qr_url: string; identifier: string; event_title: string | null } | null>(null);
  const [loadingQR, setLoadingQR] = useState(false);

  useEffect(() => {
    if (username) {
      loadProfile();
    }
  }, [username]);

  useEffect(() => {
    // Load tab data when profile is loaded and user is owner
    if (profileData?.is_owner && activeTab) {
      loadTabData(activeTab);
    }
  }, [activeTab, profileData?.is_owner]);

  const loadProfile = async () => {
    if (!username) return;
    
    setLoading(true);
    try {
      const data = await profileService.getProfile(username);
      setProfileData(data);
    } catch (error: any) {
      // Handle 403 (access denied) and 404 (not found) errors
      if (error.response?.status === 403) {
        // Profile access denied - user trying to view someone else's profile
        setProfileData(null);
      } else if (error.response?.status === 404) {
        // Profile not found
        setProfileData(null);
      } else {
        // Other errors
        console.error('Error loading profile:', error);
        alert({
          message: error.response?.data?.error || 'Failed to load profile. Please try again.',
          variant: 'danger',
        });
        setProfileData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async (tab: string) => {
    if (!profileData?.is_owner) {
      return; // Don't load data if user is not the owner
    }
    
    setLoadingData(true);
    try {
      switch (tab) {
        case 'passes':
          const passesData = await profileService.getPasses();
          console.log('Loaded passes:', passesData.passes);
          setPasses(passesData.passes || []);
          break;
        case 'bookings':
          const bookingsData = await profileService.getBookings();
          console.log('Loaded bookings:', bookingsData);
          setBookings(bookingsData || { upcoming: [], past: [] });
          break;
        case 'orders':
          const ordersData = await profileService.getOrders();
          console.log('Loaded orders:', ordersData.orders);
          setOrders(ordersData.orders || []);
          break;
        case 'addons':
          const addonsData = await profileService.getAddons();
          console.log('Loaded addons:', addonsData.addons);
          setAddons(addonsData.addons || []);
          break;
        case 'addresses':
          const addressesData = await profileService.getAddresses();
          console.log('Loaded addresses:', addressesData.addresses);
          setAddresses(addressesData.addresses || []);
          break;
      }
    } catch (error: any) {
      console.error(`Error loading ${tab}:`, error);
      alert({
        message: error.response?.data?.error || `Failed to load ${tab} data`,
        variant: 'danger',
      });
      // Set empty arrays on error to show empty state
      switch (tab) {
        case 'passes':
          setPasses([]);
          break;
        case 'bookings':
          setBookings({ upcoming: [], past: [] });
          break;
        case 'orders':
          setOrders([]);
          break;
        case 'addons':
          setAddons([]);
          break;
        case 'addresses':
          setAddresses([]);
          break;
      }
    } finally {
      setLoadingData(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleShowQR = async (pass: Pass) => {
    setSelectedPass(pass);
    setShowQRModal(true);
    setLoadingQR(true);
    
    try {
      const qrData = await profileService.getPassQRCode(pass.id);
      setQrCodeData(qrData);
    } catch (error: any) {
      alert({
        message: error.response?.data?.error || 'Failed to load QR code',
        variant: 'danger',
      });
      setShowQRModal(false);
    } finally {
      setLoadingQR(false);
    }
  };

  const handleCloseQR = () => {
    setShowQRModal(false);
    setSelectedPass(null);
    setQrCodeData(null);
  };

  const copyQRUrl = () => {
    if (qrCodeData?.qr_url) {
      navigator.clipboard.writeText(qrCodeData.qr_url);
      alert({
        message: 'QR code URL copied to clipboard!',
        variant: 'success',
      });
    }
  };

  // Generate initials for avatar fallback
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="container my-5">
        <ProfilePageSkeleton />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="container my-5">
        <Alert variant="warning">
          <Alert.Heading>Profile Access Restricted</Alert.Heading>
          <p>You can only view your own profile. Please log in to view your profile or navigate to your profile page.</p>
          {!user && (
            <div className="mt-3">
              <Link to="/login" className="btn btn-primary me-2">
                <i className="fas fa-sign-in-alt me-1"></i>
                Log In
              </Link>
              <Link to="/" className="btn btn-outline-secondary">
                <i className="fas fa-home me-1"></i>
                Go Home
              </Link>
            </div>
          )}
        </Alert>
      </div>
    );
  }

  const isOwner = profileData.is_owner;
  const fullName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || profileData.username;

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (profileData?.profile?.avatar_url) {
      return profileData.profile.avatar_url;
    }
    // Generate initials avatar using UI Avatars service
    const name = fullName || profileData?.username || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=200&bold=true`;
  };

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="container">
          <div className="row g-3 align-items-center">
            {/* Profile Picture */}
            <div className="col-auto">
              <div className="profile-avatar-wrapper">
                <img
                  src={getAvatarUrl()}
                  className="profile-avatar"
                  alt="Profile Avatar"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const name = fullName || profileData?.username || 'User';
                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=200&bold=true`;
                  }}
                />
                {isOwner && (
                  <Link
                    to={`/profile/${username}/edit`}
                    className="edit-avatar-btn"
                    title="Edit Profile"
                  >
                    <i className="fas fa-edit"></i>
                  </Link>
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="col">
              <div className="profile-info">
                <h1 className="profile-name">{fullName}</h1>
                {isOwner && (
                  <div className="profile-meta">
                    <span className="profile-email">
                      <i className="fas fa-envelope"></i>
                      <span className="text-truncate">{profileData.email}</span>
                      {profileData.email_verified && (
                        <Badge bg="success" className="ms-2">
                          <i className="fas fa-check-circle"></i>
                        </Badge>
                      )}
                    </span>
                    {profileData.profile?.phone_number && (
                      <span className="profile-phone">
                        <i className="fas fa-phone"></i>
                        {profileData.profile.phone_number}
                      </span>
                    )}
                  </div>
                )}
                {isOwner && (
                  <div className="profile-actions">
                    {!profileData.email_verified && (
                      <Button size="sm" variant="success" className="btn-compact">
                        <i className="fas fa-exclamation-circle"></i>
                        <span className="d-none d-sm-inline">Verify Email</span>
                        <span className="d-sm-none">Verify</span>
                      </Button>
                    )}
                    <Link to={`/profile/${username}/edit`} className="btn btn-sm btn-outline-light btn-compact">
                      <i className="fas fa-edit"></i>
                      <span className="d-none d-sm-inline">Edit</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            {isOwner && profileData.stats && (
              <div className="col-12 col-md-auto">
                <div className="profile-stats">
                  <div className="stat-item">
                    <div className="stat-icon">
                      <i className="fas fa-ticket-alt"></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{profileData.stats.total_passes}</div>
                      <div className="stat-label">Passes</div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-icon">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{profileData.stats.total_bookings}</div>
                      <div className="stat-label">Bookings</div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-icon">
                      <i className="fas fa-shopping-bag"></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{profileData.stats.total_orders}</div>
                      <div className="stat-label">Orders</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      {isOwner && (
        <div className="container mt-3">
          <Card className="profile-tabs-card">
            <Card.Header className="profile-tabs-header">
              <Nav variant="tabs" className="profile-tabs">
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'passes'}
                    onClick={() => setActiveTab('passes')}
                    className="flex-shrink-0"
                  >
                    <i className="fas fa-ticket me-1"></i>
                    <span className="d-none d-sm-inline">Passes</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'bookings'}
                    onClick={() => setActiveTab('bookings')}
                    className="flex-shrink-0"
                  >
                    <i className="fas fa-calendar me-1"></i>
                    <span className="d-none d-sm-inline">Bookings</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'orders'}
                    onClick={() => setActiveTab('orders')}
                    className="flex-shrink-0"
                  >
                    <i className="fas fa-shopping-bag me-1"></i>
                    <span className="d-none d-sm-inline">Orders</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'addons'}
                    onClick={() => setActiveTab('addons')}
                    className="flex-shrink-0"
                  >
                    <i className="fas fa-box me-1"></i>
                    <span className="d-none d-sm-inline">Addons</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'addresses'}
                    onClick={() => setActiveTab('addresses')}
                    className="flex-shrink-0"
                  >
                    <i className="fas fa-map-marker-alt me-1"></i>
                    <span className="d-none d-sm-inline">Addresses</span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="profile-tabs-body">
              {loadingData ? (
                <div className="py-4">
                  <SkeletonLoader type="rect" width="100%" height="150px" className="mb-3" />
                  <SkeletonLoader type="rect" width="100%" height="150px" />
                </div>
              ) : (
                <>
                  {/* Passes Tab */}
                  {activeTab === 'passes' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="text-primary mb-0">
                          <i className="fas fa-ticket me-2"></i>Active Passes
                        </h5>
                        {passes.length > 0 && (
                          <Badge bg="primary">{passes.length} Active</Badge>
                        )}
                      </div>
                      {passes.length > 0 ? (
                        <div className="row g-3">
                          {passes.map((pass) => (
                            <div key={pass.id} className="col-12 col-md-6 col-lg-4">
                              <Card className="h-100 pass-card">
                                {pass.image_url && (
                                  <Card.Img
                                    variant="top"
                                    src={pass.image_url}
                                    className="pass-image"
                                  />
                                )}
                                <Card.Body>
                                  <Card.Title className="text-truncate">
                                    {pass.event_title || 'Event'}
                                  </Card.Title>
                                  <Card.Text>
                                    <div className="pass-category-info mb-2">
                                      <div className="d-flex flex-wrap gap-1 align-items-center">
                                        <small className="text-muted">
                                          <i className="fas fa-tag me-1"></i>
                                          {pass.category_name || 'Category'}
                                        </small>
                                        {pass.cluster && (
                                          <Badge
                                            className="cluster-badge"
                                            style={{
                                              backgroundColor: pass.cluster.color || '#6c757d',
                                            }}
                                          >
                                            {pass.cluster.icon && (
                                              <i className={`${pass.cluster.icon} me-1`}></i>
                                            )}
                                            {pass.cluster.name}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <small className="text-muted">
                                      <i className="fas fa-clock me-1"></i>
                                      Expires: {formatDate(pass.expires)}
                                    </small>
                                  </Card.Text>
                                  <div className="d-flex gap-2 flex-wrap mb-2">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleShowQR(pass)}
                                      className="flex-grow-1"
                                    >
                                      <i className="fas fa-qrcode me-1"></i>
                                      View QR
                                    </Button>
                                    <Dropdown>
                                      <Dropdown.Toggle
                                        size="sm"
                                        variant="outline-secondary"
                                        className="flex-grow-1"
                                      >
                                        <i className="fas fa-download me-1"></i>
                                        Download
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        {pass.event_slug && (
                                          <>
                                            <Dropdown.Item
                                              href={`/pass/${pass.event_slug}/${pass.id}/?format=pdf`}
                                              target="_blank"
                                            >
                                              <i className="fas fa-file-pdf me-2"></i>
                                              Download PDF
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              href={`/pass/${pass.event_slug}/${pass.id}/`}
                                              target="_blank"
                                            >
                                              <i className="fas fa-image me-2"></i>
                                              Download Image
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              href={`/pass/${pass.event_slug}/${pass.id}/?action=google-wallet`}
                                              target="_blank"
                                            >
                                              <i className="fab fa-google-wallet me-2"></i>
                                              Add to Google Wallet
                                            </Dropdown.Item>
                                          </>
                                        )}
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </div>
                                  {pass.event_slug && (
                                    <Link
                                      to={`/events/${pass.event_slug}`}
                                      className="btn btn-sm btn-outline-primary w-100"
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      View Event
                                    </Link>
                                  )}
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-ticket-alt fa-3x text-muted mb-3"></i>
                          <h6 className="text-muted">No active passes found</h6>
                          <p className="text-muted mb-3">Looks like you don't have any active passes at the moment.</p>
                          <Link to="/" className="btn btn-primary btn-sm">
                            <i className="fas fa-search me-1"></i> Browse Events
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bookings Tab */}
                  {activeTab === 'bookings' && (
                    <div>
                      {bookings.upcoming.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-primary mb-3">
                            <i className="fas fa-calendar-check me-2"></i>Upcoming Bookings
                          </h5>
                          <div className="row g-3">
                            {bookings.upcoming.map((booking) => (
                              <div key={booking.id} className="col-12 col-md-6">
                                <Card className="booking-card">
                                  {booking.image_url && (
                                    <Card.Img
                                      variant="top"
                                      src={booking.image_url}
                                      className="booking-image"
                                    />
                                  )}
                                  <Card.Body>
                                    <Card.Title>{booking.activity_name || 'Activity'}</Card.Title>
                                    <Card.Text>
                                      <small className="text-muted">
                                        <i className="fas fa-calendar me-1"></i>
                                        {formatDate(booking.date)}
                                      </small>
                                      <br />
                                      <small className="text-muted">
                                        <i className="fas fa-list me-1"></i>
                                        {booking.option_name || 'Option'}
                                      </small>
                                    </Card.Text>
                                    {booking.activity_slug && (
                                      <Link
                                        to={`/activities/${booking.activity_slug}`}
                                        className="btn btn-sm btn-primary"
                                      >
                                        View Activity
                                      </Link>
                                    )}
                                  </Card.Body>
                                </Card>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {bookings.past.length > 0 && (
                        <div>
                          <h5 className="text-primary mb-3">
                            <i className="fas fa-history me-2"></i>Past Bookings
                          </h5>
                          <div className="row g-3">
                            {bookings.past.map((booking) => (
                              <div key={booking.id} className="col-12 col-md-6">
                                <Card className="booking-card">
                                  {booking.image_url && (
                                    <Card.Img
                                      variant="top"
                                      src={booking.image_url}
                                      className="booking-image"
                                    />
                                  )}
                                  <Card.Body>
                                    <Card.Title>{booking.activity_name || 'Activity'}</Card.Title>
                                    <Card.Text>
                                      <small className="text-muted">
                                        <i className="fas fa-calendar me-1"></i>
                                        {formatDate(booking.date)}
                                      </small>
                                    </Card.Text>
                                  </Card.Body>
                                </Card>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {bookings.upcoming.length === 0 && bookings.past.length === 0 && (
                        <div className="text-center py-5">
                          <i className="fas fa-calendar fa-3x text-muted mb-3"></i>
                          <h6 className="text-muted">No bookings found</h6>
                          <p className="text-muted mb-3">You don't have any bookings yet.</p>
                          <Link to="/" className="btn btn-primary btn-sm">
                            <i className="fas fa-search me-1"></i> Browse Activities
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Orders Tab */}
                  {activeTab === 'orders' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="text-primary mb-0">
                          <i className="fas fa-shopping-bag me-2"></i>Order History
                        </h5>
                      </div>
                      {orders.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>Order ID</th>
                                <th>Date</th>
                                <th>Items</th>
                                <th className="text-end">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orders.map((order) => (
                                <tr key={order.id}>
                                  <td>#{order.id}</td>
                                  <td>{formatDateTime(order.closed_at)}</td>
                                  <td>{order.items_count} item(s)</td>
                                  <td className="text-end">
                                    {order.currency.symbol}{order.total.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-shopping-bag fa-3x text-muted mb-3"></i>
                          <h6 className="text-muted">No orders found</h6>
                          <p className="text-muted mb-3">You haven't placed any orders yet.</p>
                          <Link to="/" className="btn btn-primary btn-sm">
                            <i className="fas fa-search me-1"></i> Browse Events
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Addons Tab */}
                  {activeTab === 'addons' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="text-primary mb-0">
                          <i className="fas fa-box me-2"></i>Addon Orders
                        </h5>
                      </div>
                      {addons.length > 0 ? (
                        <div className="row g-3">
                          {addons.map((addon) => (
                            <div key={addon.id} className="col-12 col-md-6 col-lg-4">
                              <Card className="addon-card">
                                {addon.image_url && (
                                  <Card.Img
                                    variant="top"
                                    src={addon.image_url}
                                    className="addon-image"
                                  />
                                )}
                                <Card.Body>
                                  <Card.Title>{addon.addon_name}</Card.Title>
                                  <Card.Text>
                                    <small className="text-muted">
                                      Quantity: {addon.quantity}
                                    </small>
                                    <br />
                                    <small className="text-muted">
                                      Date: {formatDate(addon.sale_date)}
                                    </small>
                                    <br />
                                    <strong>Total: ${addon.total_price.toFixed(2)}</strong>
                                    {addon.delivered && (
                                      <Badge bg="success" className="ms-2">Delivered</Badge>
                                    )}
                                  </Card.Text>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-box fa-3x text-muted mb-3"></i>
                          <h6 className="text-muted">No addon orders found</h6>
                          <p className="text-muted mb-3">You haven't purchased any addons yet.</p>
                          <Link to="/" className="btn btn-primary btn-sm">
                            <i className="fas fa-search me-1"></i> Browse Events
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Addresses Tab */}
                  {activeTab === 'addresses' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="text-primary mb-0">
                          <i className="fas fa-map-marker-alt me-2"></i>Addresses
                        </h5>
                        <Button size="sm" variant="primary">
                          <i className="fas fa-plus me-1"></i> Add Address
                        </Button>
                      </div>
                      {addresses.length > 0 ? (
                        <div className="row g-3">
                          {addresses.map((address) => (
                            <div key={address.id} className="col-12 col-md-6">
                              <Card className="address-card">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <Card.Title>
                                        {address.name}
                                        {address.is_default && (
                                          <Badge bg="primary" className="ms-2">Default</Badge>
                                        )}
                                      </Card.Title>
                                      <Card.Text>
                                        <small>{address.string_address}</small>
                                        <br />
                                        <small className="text-muted">
                                          <i className="fas fa-phone me-1"></i>
                                          {address.phone}
                                        </small>
                                      </Card.Text>
                                    </div>
                                    <div>
                                      <Button size="sm" variant="outline-primary" className="me-2">
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                      <Button size="sm" variant="outline-danger">
                                        <i className="fas fa-trash"></i>
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-map-marker-alt fa-3x text-muted mb-3"></i>
                          <h6 className="text-muted">No addresses found</h6>
                          <p className="text-muted mb-3">You haven't added any addresses yet.</p>
                          <Button variant="primary" size="sm">
                            <i className="fas fa-plus me-1"></i> Add Address
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      {/* QR Code Modal */}
      <Modal show={showQRModal} onHide={handleCloseQR} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-qrcode me-2"></i>
            QR Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {loadingQR ? (
            <div className="py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading QR code...</p>
            </div>
          ) : qrCodeData ? (
            <>
              {selectedPass && (
                <h6 className="mb-3">{selectedPass.event_title || 'Event Pass'}</h6>
              )}
              <div className="mb-3">
                <img
                  src={qrCodeData.qr_code}
                  alt="QR Code"
                  className="img-fluid"
                  style={{ maxWidth: '300px', width: '100%' }}
                />
              </div>
              <p className="text-muted small mb-2">
                <strong>Identifier:</strong> {qrCodeData.identifier}
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={copyQRUrl}
                >
                  <i className="fas fa-copy me-1"></i>
                  Copy URL
                </Button>
                {selectedPass?.event_slug && (
                  <Button
                    variant="primary"
                    size="sm"
                    href={`/pass/${selectedPass.event_slug}/${selectedPass.id}/?format=pdf`}
                    target="_blank"
                  >
                    <i className="fas fa-download me-1"></i>
                    Download PDF
                  </Button>
                )}
              </div>
            </>
          ) : (
            <Alert variant="danger">Failed to load QR code</Alert>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProfilePage;

